import { ApolloError } from '@apollo/client';

export const DEFAULT_ERROR_MESSAGE =
  'It appears something went wrong. Please contact application team if this problem persists.';

export const DEFAULT_NETWORK_ERROR_MESSAGE =
  'Unable to connect to the server. Check internet connection or contact application team if this problem persists.';

export const PERMISSION_ERROR_MESSAGE =
  'You do not have adequate permissions to view this page. ' +
  'Please contact your account manager to request the right permissions';

export const ENTITY_NOT_FONUD = 'The requested entity was not found. Please verify your URL is correct';

/**
 * Convert error object to meaningful error message
 */
export function convertSystemErrorToString(error: any): string {
  let message = getMessageFromError(error);
  if (message === null) {
    message = getMessageFromGraphQLError(error);
  }
  if (message == null) {
    return DEFAULT_ERROR_MESSAGE;
  }
  return sanitizeErrorMessage(message);
}

/**
 * Maps GQL error to meaningful error message. Uses the first error if there are multiple.
 */
export const formatApolloError = (error: ApolloError): string => {
  const { graphQLErrors, networkError } = error;

  if (networkError) {
    return DEFAULT_NETWORK_ERROR_MESSAGE;
  }

  if (graphQLErrors && graphQLErrors.length > 0) {
    const firstStatusCode = (graphQLErrors[0]?.extensions?.exception as any)?.statusCode || null;
    return statusCodeToErrorMessage(firstStatusCode) || DEFAULT_ERROR_MESSAGE;
  }

  return DEFAULT_ERROR_MESSAGE;
};

/**
 * Get message from error.message
 */
function getMessageFromError(error: any): string | null {
  if (
    error &&
    error.message &&
    typeof error.message === 'string' &&
    error.message !== 'null' &&
    error.message.toLowerCase() !== 'graphql error: null'
  ) {
    return error.message;
  }
  return null;
}

/**
 * Get message from error.errors[0].message. In case message is null, try to get statusCode
 */
function getMessageFromGraphQLError(error: any): string | null {
  let graphQLError;
  if (error && Array.isArray(error.errors) && error.errors.length > 0) {
    graphQLError = error.errors[0];
  } else if (error && Array.isArray(error.graphQLErrors) && error.graphQLErrors.length > 0) {
    graphQLError = error.graphQLErrors[0];
  }

  if (graphQLError) {
    const errorMessage = getMessageFromError(graphQLError);
    if (errorMessage !== null) {
      return errorMessage;
    }

    if (typeof (graphQLError.extensions?.exception as any)?.statusCode === 'number') {
      return statusCodeToErrorMessage((graphQLError.extensions.exception as any).statusCode);
    }
  }
  return null;
}

function sanitizeErrorMessage(message: string): string {
  if (message.includes('is not authorized to perform:')) {
    return PERMISSION_ERROR_MESSAGE;
  }

  if (/GraphQL error:/i.test(message)) {
    return message.substring('GraphQL error:'.length).trim();
  }

  return message;
}

/**
 * TODO: add more status code
 */
function statusCodeToErrorMessage(statusCode: number): string | null {
  if (statusCode === 401 || statusCode === 403) {
    return PERMISSION_ERROR_MESSAGE;
  }

  if (statusCode === 404) {
    return ENTITY_NOT_FONUD;
  }
  return null;
}
